import { z } from 'zod';
import { backorderStatusSchema } from '../backorder/status.js';
import { unitSchema } from '../catalog/unit.js';
import { requisitionStatusSchema } from '../requisition/status.js';
/**
 * Hisobot davri — barcha `/reports/*` endpointlari uchun BITTA qoida.
 *
 * Chegara (`MAX_RANGE_DAYS`) qattiq va ataylab: usiz bitta so'rov butun
 * harakat jurnalini skanerlab bazani cho'ktirishi mumkin edi.
 * `paginationQuerySchema` dagi `max(200)` bilan ayni sabab — chegarasiz
 * so'rov bir kun albatta yoziladi, va u ishlab turgan tizimni to'xtatadi.
 */
export const MAX_RANGE_DAYS = 366;
/** Standart davr — oxirgi 30 kun. */
export const DEFAULT_RANGE_DAYS = 30;
const MS_PER_DAY = 86_400_000;
/** `YYYY-MM-DD` — vaqt mintaqasiz, chunki hisobot KUNLAR bo'yicha o'qiladi. */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Sana YYYY-MM-DD ko\'rinishida bo\'lishi kerak')
    .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Sana mavjud emas');
/** Sanani UTC yarim tunga aylantiradi — kun chegarasi ikki tomonda bir xil. */
const atUtcMidnight = (value) => Date.parse(`${value}T00:00:00Z`);
/** Bugundan `days` kun oldingi sana, `YYYY-MM-DD`. */
export const isoDaysAgo = (days, now = new Date()) => {
    const shifted = new Date(now.getTime() - days * MS_PER_DAY);
    return shifted.toISOString().slice(0, 10);
};
/** Bugungi sana, `YYYY-MM-DD`. */
export const isoToday = (now = new Date()) => now.toISOString().slice(0, 10);
/**
 * Davr maydonlari — o'z so'rovini qurayotgan endpoint shuni yoyadi.
 *
 * Ajratib qo'yilgani uchun davr QOIDASI bitta joyda qoladi: `reportRangeSchema`
 * ning o'zi ham, qo'shimcha maydoni bor so'rovlar ham (`flowQuerySchema`) shu
 * uchlikdan quriladi. Nusxa ko'chirilsa, bir kun biri 366 kunni, ikkinchisi
 * boshqa sonni cheklab turardi.
 */
export const reportRangeShape = {
    from: isoDate.optional(),
    to: isoDate.optional(),
};
/**
 * Standart qiymatlar `.default()` bilan emas, shu yerda beriladi: ikkalasi
 * BIR-BIRIGA bog'liq — faqat `from` kelsa, `to` bugun bo'lishi kerak.
 *
 * Qolgan maydonlar O'Z HOLICHA o'tadi, shuning uchun qo'shimcha maydonli
 * so'rovlar ham shu transformatsiyadan foydalanadi.
 */
export const withRangeDefaults = (value) => ({
    ...value,
    from: value.from ?? isoDaysAgo(DEFAULT_RANGE_DAYS),
    to: value.to ?? isoToday(),
});
/**
 * Davr chegaralari (`checkRackBayWidths` bilan bir uslub — `superRefine` uchun).
 *
 * Ikki tekshiruv MUSTAQIL: `to < from` bo'lsa, ayirma manfiy chiqadi va
 * uzunlik sharti o'z-o'zidan o'tadi, ya'ni bitta xatoga ikki xabar chiqmaydi.
 */
export const checkReportRange = (value, ctx) => {
    if (atUtcMidnight(value.to) < atUtcMidnight(value.from)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: 'Tugash sanasi boshlanish sanasidan oldin bo\'lishi mumkin emas',
        });
    }
    if ((atUtcMidnight(value.to) - atUtcMidnight(value.from)) / MS_PER_DAY > MAX_RANGE_DAYS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `Davr ${MAX_RANGE_DAYS} kundan uzun bo'lishi mumkin emas`,
        });
    }
};
export const reportRangeSchema = z
    .object(reportRangeShape)
    .transform(withRangeDefaults)
    .superRefine(checkReportRange);
/* ── Kirim/chiqim dinamikasi ─────────────────────────────────────────── */
export const stockFlowPointSchema = z.object({
    /** `YYYY-MM-DD` — kun. Harakat bo'lmagan kun ham qatorda BO'LADI (nol bilan). */
    date: z.string(),
    inQty: z.number(),
    outQty: z.number(),
});
export const stockFlowReportSchema = z.object({
    series: z.array(stockFlowPointSchema),
    totals: z.object({ inQty: z.number(), outQty: z.number() }),
});
/* ── Mahsulotlar TOP-10 ──────────────────────────────────────────────── */
export const topItemSchema = z.object({
    nomenclatureId: z.string(),
    code: z.string(),
    name: z.string(),
    unit: z.string(),
    qty: z.number(),
});
export const lowStockItemSchema = topItemSchema.extend({
    minStock: z.number(),
});
export const topItemsReportSchema = z.object({
    /** Davr ichida eng ko'p harakatdagi mahsulotlar. */
    moved: z.array(topItemSchema),
    /** Qoldig'i eng past — `qty < minStock`, faqat `minStock > 0` bo'lganlar. */
    low: z.array(lowStockItemSchema),
});
/* ── Talabnomalar holati ─────────────────────────────────────────────── */
export const requisitionReportSchema = z.object({
    byStatus: z.array(z.object({ status: requisitionStatusSchema, count: z.number().int() })),
    total: z.number().int(),
    /** Yakunlanmagan (`CLOSED`/`CANCELLED` bo'lmagan) talabnomalar soni. */
    openCount: z.number().int(),
    /**
     * Yopilgan talabnomaning o'rtacha umri, kunlarda.
     *
     * `null` — davr ichida birorta ham yopilmagan. Nol EMAS: «hali ma'lumot
     * yo'q» bilan «bir kunda bajarilgan» ni aralashtirib bo'lmaydi.
     */
    avgCycleDays: z.number().nullable(),
});
/* ── Bo'linmalar ombori ──────────────────────────────────────────────── */
export const subdivisionReportRowSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    /** Egasining nomi — bo'linma YOLG'IZ ko'rsatilmaydi («R va AX» ko'p). */
    orgUnitName: z.string(),
    /** Nechta turdagi partiya turibdi. */
    itemCount: z.number().int(),
    /** Umumiy miqdor — birliklar aralash, shuning uchun faqat TARTIBLASH uchun. */
    totalQty: z.number(),
});
export const subdivisionReportQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(10),
});
/* ── Birlik bo'yicha miqdor ──────────────────────────────────────────── */
/**
 * BITTA birlikdagi miqdor.
 *
 * Nega kerak: ombor miqdorlari ARALASH birlikda yashaydi — dona, metr,
 * litr, komplekt. Ularni qo'shib bitta son chiqarish oson, lekin natija
 * hech narsani anglatmaydi: «235» degan son 220 dona bolt bilan 15 litr
 * moyning yig'indisi bo'lishi mumkin va uni na buyurtma qilish, na
 * mashinaga yuklash uchun ishlatib bo'ladi.
 *
 * Shuning uchun odam O'QIYDIGAN joylarda miqdor har doim birlik bilan
 * ajratilgan holda beriladi. Aralash yig'indi faqat TARTIBLASH uchun
 * qoladi va u turgan joyda shundayligi aytib o'tiladi.
 */
export const unitQtySchema = z.object({
    unit: unitSchema,
    qty: z.number(),
});
/**
 * Satrlarni BIRLIK bo'yicha yig'adi, ko'pdan kamga.
 *
 * Shartnomada turadi, ekranda emas: ayni qoida serverning testida ham,
 * ikkita alohida ekranda ham qo'llanadi va uch marta yozilsa, ular bir
 * kun uch xil tartibda chiqara boshlardi.
 */
export const unitTotalsOf = (lines) => {
    const byUnit = new Map();
    for (const line of lines)
        byUnit.set(line.unit, (byUnit.get(line.unit) ?? 0) + line.qty);
    return [...byUnit.entries()]
        .map(([unit, qty]) => ({ unit, qty: Math.round(qty * 1000) / 1000 }))
        .sort((a, b) => b.qty - a.qty);
};
/* ── Omborlar kesimi ─────────────────────────────────────────────────── */
/**
 * BITTA OMBOR — bitta satr.
 *
 * Yig'ma raqamdan farqi shu: MTU rahbarida o'nlab ombor bor va «jami
 * 18 000 dona» degan son unga hech narsa aytmaydi — qaysi omborda tiqilib
 * qolgani ko'rinmaydi. Kesim esa savolni «qayerda?» ga aylantiradi.
 *
 * Doira ALOHIDA maydon emas: `report.view` doirasi satrlarni o'zi filtrlaydi
 * — MTU rahbari butun mintaqani, korxona rahbari o'z omborlarini oladi.
 */
export const warehouseReportRowSchema = z.object({
    warehouseId: z.string(),
    code: z.string(),
    name: z.string(),
    /** Egasi — ombor YOLG'IZ ko'rsatilmaydi («Markaziy ombor» ko'p bo'ladi). */
    orgUnitName: z.string(),
    /** Nechta TURDAGI mahsulot turibdi (nomenklatura soni). */
    itemCount: z.number().int(),
    /** Umumiy miqdor — birliklar aralash, shuning uchun faqat TARTIBLASH uchun. */
    totalQty: z.number(),
    /**
     * Band qilingan, hali berilmagan miqdor.
     *
     * «Olib ketilmagan mahsulot» aynan shu: sklad talabnomani qabul qilgan
     * paytdan boshlab miqdor javonda turadi, lekin boshqa hech kimga berib
     * bo'lmaydi. Qaysi bosqichda turgani `pipelineReport` da ko'rinadi.
     */
    reservedQty: z.number(),
    /** Ombordagi jami katak. */
    cellCount: z.number().int(),
    /** Ichida mahsulot bor katak — bandlik foizining MAXRAJI `cellCount`. */
    usedCellCount: z.number().int(),
    /** Chegaradan pastga tushgan nomlar soni (`minStock > 0` bo'lganlar). */
    lowCount: z.number().int(),
    /** Oxirgi kirim sanasi, ISO. `null` — bu omborga hech narsa kelmagan. */
    lastReceiptAt: z.string().nullable(),
});
export const warehouseReportSchema = z.object({
    rows: z.array(warehouseReportRowSchema),
    /**
     * Jami — satrlar YIG'INDISI emas.
     *
     * `itemCount` satrlar bo'yicha qo'shib bo'lmaydi: bitta bolt ikki omborda
     * tursa, u ikki satrda ham sanaladi, mintaqada esa BITTA nom. Shuning
     * uchun jami alohida hisoblanadi.
     */
    totals: z.object({
        warehouseCount: z.number().int(),
        itemCount: z.number().int(),
        totalQty: z.number(),
        reservedQty: z.number(),
        /**
         * Band qilingan miqdor BIRLIKLAR bo'yicha, ko'pdan kamga.
         *
         * Ekranda aynan shu ko'rsatiladi; yuqoridagi `reservedQty` esa
         * aralash yig'indi va u faqat «umuman band narsa bormi» degan
         * savolga javob beradi (`unitQtySchema` izohiga qarang).
         */
        reservedByUnit: z.array(unitQtySchema),
        lowCount: z.number().int(),
    }),
});
/* ── Tushum turlari ──────────────────────────────────────────────────── */
/**
 * Kirim qaysi HUJJAT sababli bo'lgani.
 *
 * `MOVEMENT_DOC_TYPES` ning o'zi ishlatilmaydi va bu ataylab: u yerda
 * `MOVE` ham bor, ko'chirish esa omborga hech narsa keltirmaydi. Grafikda
 * u ko'rinsa, javonlarni tartibga solgan kun «katta tushum» bo'lib turardi.
 */
export const RECEIPT_SOURCES = ['RECEIPT', 'REQUISITION', 'ADJUSTMENT'];
export const receiptSourceSchema = z.enum(RECEIPT_SOURCES);
const RECEIPT_SOURCE_LABELS = {
    RECEIPT: 'Kirim hujjati',
    REQUISITION: 'Talabnoma qaytimi',
    ADJUSTMENT: 'Tuzatish',
};
export const receiptSourceLabel = (source) => RECEIPT_SOURCE_LABELS[source];
export const receiptPointSchema = z.object({
    /** `YYYY-MM-DD`. Tushum bo'lmagan kun ham qatorda BO'LADI (nollar bilan). */
    date: z.string(),
    receipt: z.number(),
    requisition: z.number(),
    adjustment: z.number(),
});
export const receiptTypeReportSchema = z.object({
    series: z.array(receiptPointSchema),
    totals: z.object({
        receipt: z.number(),
        requisition: z.number(),
        adjustment: z.number(),
    }),
    /** Nechta kirim HARAKATI bo'lgan — miqdor emas, hodisa soni. */
    eventCount: z.number().int(),
});
/* ── Jarayon voronkasi ───────────────────────────────────────────────── */
/**
 * Bitta bosqichda HOZIR nechta hujjat turibdi.
 *
 * Davr bilan kesilmaydi: «qayerda tiqilib qoldi» degan savolning o'tgan oy
 * bilan aloqasi yo'q. `oldestDays` esa aybni aniqlaydi — soni kichik, lekin
 * 40 kun turgan bosqich soni katta, lekin bugun to'lgan bosqichdan yomonroq.
 */
export const pipelineStageSchema = z.object({
    status: requisitionStatusSchema,
    count: z.number().int(),
    /** Eng uzoq kutayotganining yoshi, kunlarda. `null` — bosqich bo'sh. */
    oldestDays: z.number().nullable(),
});
/** «Olib ketilmagan» hujjatning bitta POZITSIYASI. */
export const awaitingLineSchema = z.object({
    nomenclatureId: z.string(),
    code: z.string(),
    name: z.string(),
    unit: unitSchema,
    qty: z.number(),
});
/**
 * «Olib ketilmagan» ro'yxatining BITTA satri.
 *
 * Son o'z-o'zicha amalga olib bormaydi: rahbar «1 240 dona band» ni
 * ko'rgach darrov «kim so'ragan va kim olib ketmayapti?» deb so'raydi.
 * Javob shu yerda — hujjat raqami, muallifi, korxonasi, bo'limi va
 * necha kundan beri kutayotgani.
 */
export const awaitingPickupRowSchema = z.object({
    requisitionId: z.string(),
    docNo: z.string(),
    status: requisitionStatusSchema,
    /** Talabnomani KIM ochgan. */
    requesterName: z.string(),
    /** Qaysi KORXONA. `null` — talabnoma korxonasiz yozilgan (eski hujjat). */
    orgUnitName: z.string().nullable(),
    /** Qaysi BO'LIM (bo'linma). `null` — bo'lim ko'rsatilmagan. */
    subdivisionName: z.string().nullable(),
    warehouseName: z.string(),
    /**
     * «Через кого» — mahsulotni olib ketishi kerak bo'lgan odam.
     *
     * Matn, foydalanuvchiga havola emas: olib ketadigan odamning ko'pincha
     * tizimda hisobi yo'q (haydovchi, ishchi).
     */
    throughPerson: z.string().nullable(),
    /** Shu bosqichda necha kundan beri turibdi. */
    waitingDays: z.number(),
    /**
     * Joylashtirilgan (band) POZITSIYALAR.
     *
     * Ro'yxatning o'zi qaytadi, yig'indisi emas: satrda undan qisqa xulosa
     * chiziladi (`unitTotalsOf`), oynada esa har pozitsiya alohida ko'rinadi
     * — «nega olmadingiz?» degan savolga javob aynan shu yerda: odam
     * qaysi mahsulot haqida gapirayotganini bilishi kerak.
     */
    lines: z.array(awaitingLineSchema),
    /** So'rovchining lavozimi. `null` — aktiv ish joyi yo'q. */
    requesterPosition: z.string().nullable(),
    /**
     * So'rovchining telefoni — «nega olmadingiz?» deb so'rash uchun.
     *
     * `null` odatiy hol: telefon majburiy maydon emas. Shunda oynada
     * raqam o'rniga uning yo'qligi aytiladi — bo'sh katak «yuklanmadi»
     * deb o'qilardi.
     */
    requesterPhone: z.string().nullable(),
    /** Nima uchun so'ralgan — suhbatni boshlash uchun kerak bo'ladigan kontekst. */
    purpose: z.string(),
    /** Qachonga kerak edi (ISO). `null` — muddat ko'rsatilmagan. */
    neededBy: z.string().nullable(),
});
/** Ro'yxat uzunligi — panel javob emas, yo'l ko'rsatkichi. */
export const PIPELINE_AWAITING_PREVIEW = 12;
export const pipelineReportSchema = z.object({
    /** Faqat YAKUNLANMAGAN holatlar, oqim tartibida. */
    stages: z.array(pipelineStageSchema),
    openCount: z.number().int(),
    /**
     * Sklad qabul qilgan, lekin hali berilmagan talabnomalar soni.
     *
     * Miqdori (`reservedQty`) omborlar kesimida turadi: u qoldiqdan
     * hisoblanadi va talabnoma kontekstida yo'q.
     */
    awaitingPickupCount: z.number().int(),
    /**
     * O'sha talabnomalarning eng UZOQ kutayotgan qismi.
     *
     * Ro'yxat qisqa (`PIPELINE_AWAITING_PREVIEW`) va eng keksasidan
     * boshlanadi: hammasi kerak bo'lsa talabnomalar sahifasi bor, bu
     * yerda esa «kimga qo'ng'iroq qilish kerak» degan savolga javob.
     */
    awaiting: z.array(awaitingPickupRowSchema),
});
/* ── Korxonalar reytingi ─────────────────────────────────────────────── */
export const orgUnitReportRowSchema = z.object({
    orgUnitId: z.string(),
    name: z.string(),
    /** Davr ichida ochilgan talabnoma soni. */
    requested: z.number().int(),
    /** Shulardan yopilgani. */
    closed: z.number().int(),
    /** Hali yakunlanmagani. */
    openCount: z.number().int(),
    /** Rad etilgani — «ko'p so'rab, ko'p rad etilgan» korxona darrov ko'rinadi. */
    rejected: z.number().int(),
    /** Yopilganlarning o'rtacha umri, kunlarda. `null` — birortasi yopilmagan. */
    avgCycleDays: z.number().nullable(),
});
export const orgUnitReportSchema = z.object({
    rows: z.array(orgUnitReportRowSchema),
});
/* ── Bildirgilar (qoldiqda yo'q mahsulot) ────────────────────────────── */
export const backorderStageSchema = z.object({
    status: backorderStatusSchema,
    count: z.number().int(),
    oldestDays: z.number().nullable(),
});
/**
 * Bildirgilar voronkasi — talabnomanikidan ALOHIDA so'rov.
 *
 * Ikkovi bitta endpointga qo'shilmaydi: bildirgi o'z konteksti va o'z
 * jadvalida yashaydi, bitta so'rov esa ikki kontekstni bir repozitoriyga
 * bog'lab qo'yardi. Ekranda esa ular yonma-yon turadi — savol bitta:
 * «so'rov qayerda tiqilib qoldi?»
 */
export const backorderReportSchema = z.object({
    stages: z.array(backorderStageSchema),
    openCount: z.number().int(),
});
//# sourceMappingURL=report.schema.js.map